import cn from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import CTA from 'react-scale/components/ui/cta';
import Tabs from 'react-scale/components/ui/tabs';
import Link from 'next/link';

const PricingTable = ({ labels, tabs, colsNumber }) => {
  const rows = labels?.map((label, idx) => ({
    label: label.name,
    entries: tabs?.map(tab => tab.entries[idx]?.text || '')
  }));

  const isPriceRegex = /(?=.)^\$(([1-9][0-9]{0,2}(,[0-9]{3})*)|[0-9]+)?(\.[0-9]{1,2})?$/;

  return (
    <>
      <div className="hidden lg:flex flex-col">
        <div
          className={cn('grid', `grid-cols-${colsNumber}`, {
            'gap-8': colsNumber <= 3,
            'gap-4': colsNumber > 3
          })}
        >
          <div />
          {tabs?.map(tab => (
            <div
              key={tab.title}
              className={cn(
                `bg-${tab.primaryColor}`,
                'pb-0 p-[1px] font-normal rounded-t-lg flex flex-col'
              )}
            >
              <div className="flex flex-col rounded-t-lg bg-black flex-1">
                <p className="uppercase text-center bg-gray-900 text-xs xl:text-sm leading-6 tracking-[4px] rounded-t-lg py-3">
                  {tab.overtitle}
                </p>
                <div className="flex flex-col justify-between flex-1">
                  <div className="flex flex-col justify-between flex-1 p-8 my-[1px]">
                    <div>
                      <p
                        className={cn(`text-${tab.secondaryColor}`, 'text-left', {
                          'text-xl': colsNumber < 4,
                          'text-lg': colsNumber === 4
                        })}
                      >
                        {tab.title}
                      </p>
                      {tab.description && (
                        <p className="text-left text-base text-white mt-2">{tab.description}</p>
                      )}
                    </div>
                    {tab.cta && tab.showCtaOnTop && (
                      <div className="mt-6">
                        <CTA cta={tab.cta} size="small" />
                      </div>
                    )}
                  </div>
                </div>
                {tab.features?.length > 0 && (
                  <div className="h-full px-8 py-10 space-y-4 text-left bg-gray-900">
                    <p
                      className={cn(
                        `text-${tab.primaryColor?.substr(0, tab.primaryColor.length - 2)}`,
                        'tracking-[1px] max-w-max'
                      )}
                    >
                      Best option if:
                    </p>
                    <ul className="space-y-[10px] pl-1">
                      {tab.features?.map(({ text }) => (
                        <li
                          className={cn('flex items-baseline text-sm leading-6', {
                            'space-x-4': colsNumber < 4,
                            'space-x-2': colsNumber === 4
                          })}
                        >
                          <div
                            className={cn(
                              `bg-${tab.secondaryColor}`,
                              'w-[10px] h-[10px] relative rounded-full'
                            )}
                          />
                          <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            className="flex-1 text-sm leading-6 text-gray-300"
                          >
                            {text}
                          </ReactMarkdown>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div>
          {rows?.map(row => (
            <div
              key={row.label}
              className={cn(
                'grid border-b border-gray-700 first:border-t',
                `grid-cols-${colsNumber}`,
                {
                  'gap-8': colsNumber <= 3,
                  'gap-4': colsNumber > 3
                }
              )}
            >
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                className={cn('py-6 text-base 3xl:text-xl leading-6 my-auto')}
                components={{
                  span({ children }) {
                    return <span className="text-sm">{children}</span>;
                  }
                }}
              >
                {row?.label}
              </ReactMarkdown>
              {row?.entries?.map((entry, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={cn('px-[1px]', `bg-${tabs[index].primaryColor}`)}
                >
                  <div className={cn('bg-gray-900 h-full items-center')}>
                    <ReactMarkdown
                      rehypePlugins={[rehypeRaw]}
                      className={cn(
                        'bg-gray-900 py-6 text-gray-300 text-left px-8 leading-6 flex h-full items-center',
                        {
                          'text-base': !isPriceRegex.test(entry),
                          'text-lg': isPriceRegex.test(entry)
                        }
                      )}
                      components={{
                        a({ children, href }) {
                          return (
                            <Link href={href} passHref>
                              <a className="text-scale-mauve">{children}</a>
                            </Link>
                          );
                        }
                      }}
                    >
                      {entry}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        {tabs.some(tab => tab.ctaDescription) && (
          <div
            className={cn('grid', `grid-cols-${colsNumber}`, {
              'gap-8': colsNumber <= 3,
              'gap-4': colsNumber > 3
            })}
          >
            <div> </div>
            <>
              {tabs.map((tab, idx) =>
                tab.ctaDescription ? (
                  <div key={tab.title}>
                    <div
                      className={cn(
                        'flex rounded-b-lg flex-col justify-between px-[1px] pb-[1px] h-full',
                        `bg-${tabs[idx].primaryColor}`
                      )}
                    >
                      <div className="px-8 py-6 bg-black h-full flex flex-col justify-between flex-1 rounded-b-lg">
                        <div>
                          <p className="text-sm text-white mb-6 leading-6">{tab.ctaDescription}</p>
                        </div>
                        {tab.cta && (
                          <CTA
                            cta={{
                              buttons: tab.cta?.buttons && tab.cta?.buttons,
                              hubspotModal: tab.cta?.hubspotModal?.label && tab.cta?.hubspotModal
                            }}
                            size="small"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div> </div>
                )
              )}
            </>
          </div>
        )}
      </div>
      <PricingMobile tabs={tabs} labels={labels} colsNumber={colsNumber} />
    </>
  );
};

const PricingMobile = ({ tabs, labels }) => {
  const isPriceRegex = /(?=.)^\$(([1-9][0-9]{0,2}(,[0-9]{3})*)|[0-9]+)?(\.[0-9]{1,2})?$/;

  return (
    <div className="lg:hidden">
      <Tabs
        tabs={tabs?.map(tab => ({
          title: tab.title ? tab.title : tab.overtitle,
          content: (
            <div>
              <div className="lg:min-w-sm lg:max-w-sm xl:min-w-[488px] xl:max-w-[488px]">
                <h2 className="mb-5 text-2xl leading-10 lg:mb-2 lg:text-3xl">{tab.overtitle}</h2>
                <p className="mb-4 text-gray-100">{tab.description}</p>
                {tab.cta && tab.showCtaOnTop && <CTA cta={tab.cta} size="small" />}
                {tab.features?.length > 0 && (
                  <>
                    <p className="mt-8 mb-6">This is your best option if:</p>
                    <ul className="lg:max-w-[416px]">
                      {tab.features?.map(({ text }) => (
                        <li className="flex items-baseline mb-8 space-x-4">
                          <div
                            className={cn(
                              `bg-${tab.secondaryColor}`,
                              'w-4 h-4 top-[3px] relative rounded-full'
                            )}
                          />
                          <ReactMarkdown
                            rehypePlugins={[rehypeRaw]}
                            className="flex-1 text-gray-300"
                          >
                            {text}
                          </ReactMarkdown>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="mt-24 lg:mt-0 lg:ml-[36px] w-full lg:py-6 xl:py-8 lg:px-8 xl:px-10 lg:bg-gray-900 rounded-lg">
                {labels?.map((label, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={idx} className="pb-10 border-b border-tundora">
                    <p className="mt-10 mb-6 text-sm leading-6 sm:text-base">{label.name}</p>
                    <p
                      className={cn(
                        'text-center py-6 bg-gray-800 rounded-lg',
                        tab === 'Enterprise' && 'hidden'
                      )}
                    >
                      <ReactMarkdown
                        rehypePlugins={[rehypeRaw]}
                        className={cn('text-left px-8 leading-6 flex h-full items-center', {
                          'text-base': !isPriceRegex.test(tab.entries[idx]),
                          'text-lg': isPriceRegex.test(tab.entries[idx])
                        })}
                        components={{
                          a({ children, href }) {
                            return (
                              <Link href={href} passHref>
                                <a className="text-scale-mauve">{children}</a>
                              </Link>
                            );
                          }
                        }}
                      >
                        {tab.entries[idx].text}
                      </ReactMarkdown>
                    </p>
                  </div>
                ))}
              </div>
              {tab.ctaDescription && (
                <>
                  <p className="text-sm text-white mb-6 mt-16">{tab.ctaDescription}</p>
                  {tab.cta && (
                    <CTA
                      cta={{
                        buttons: tab.cta?.buttons && tab.cta?.buttons,
                        hubspotModal: tab.cta?.hubspotModal?.label && tab.cta?.hubspotModal
                      }}
                      size="small"
                    />
                  )}
                </>
              )}
            </div>
          )
        }))}
      />
    </div>
  );
};

const Pricing = ({ table, disclaimer }) => {
  const colsNumber = table?.tabs?.length + 1 || 0;
  return (
    <>
      <PricingTable tabs={table?.tabs} labels={table?.labels} colsNumber={colsNumber} />
      <p className="text-sm leading-6 text-gray-500 mt-36 lg:mt-20 lg:leading-7">{disclaimer}</p>
    </>
  );
};

export default Pricing;
